import { useState, useRef, useEffect, useMemo } from "react";
import Icon1 from "@assets/icons/Icon";
import { colors } from "sexy";
import { useInAppNotificationsStore } from "@store/inAppNotificationsStore";
import type { InAppNotification } from "@store/inAppNotificationsStore";
import { navigateTo } from "context/navigation.service";

const EVENT_ICONS: Record<string, string> = {
  payment_received: "facture",
  booking_created: "planning",
  document_signed: "sign",
  new_message: "message",
  devis_signed: "devis",
  ordre_status_changed: "settings",
  avis_received: "star",
  devis_auto_cancelled: "effacer2",
  mention: "nouveau-message",
};

const EVENT_COLORS: Record<string, string> = {
  payment_received: colors.emerald_500,
  booking_created: colors.blue_500,
  document_signed: colors.violet_500,
  new_message: colors.neutral_500,
  devis_signed: colors.orange_500,
  ordre_status_changed: colors.indigo_500,
  avis_received: colors.amber_500,
  devis_auto_cancelled: colors.red_500,
  mention: colors.blue_600,
};

const TOAST_DURATION = 5000;

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "à l'instant";
  if (mins < 60) return `il y a ${mins} min`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `il y a ${hrs}h`;
  const days = Math.floor(hrs / 24);
  return `il y a ${days}j`;
}

/** Extract a grouping key from a notification href, or null if ungroupable */
function getGroupKey(notif: InAppNotification): string | null {
  // Ordre notifications: /ordre/{id}
  const ordreMatch = notif.href.match(/^\/ordre\/([^/?]+)/);
  if (ordreMatch) return `ordre:${ordreMatch[1]}`;
  // Message notifications: /messagerie?...&discussion={id}
  if (notif.type === "new_message") {
    const discMatch = notif.href.match(/[?&]discussion=([^&]+)/);
    if (discMatch) return `discussion:${discMatch[1]}`;
  }
  return null;
}

/** Extract a display label for a group of notifications */
function extractGroupLabel(groupKey: string, notifications: InAppNotification[]): string {
  if (groupKey.startsWith("discussion:")) {
    // Message body format: "SenderName dans DiscName : preview..."
    for (const n of notifications) {
      const match = n.body.match(/dans (.+?) :/);
      if (match) return match[1];
    }
    return "Discussion";
  }
  // Ordre group: try to find #XXXX pattern
  for (const n of notifications) {
    const numMatch = n.body.match(/#(\d+)/);
    if (numMatch) {
      const fullMatch = n.body.match(/#\d+\s*\(([^)]+)\)/);
      if (fullMatch) return `#${numMatch[1]} — ${fullMatch[1]}`;
      return `Ordre #${numMatch[1]}`;
    }
  }
  for (const n of notifications) {
    if (n.type === "booking_created" || n.type === "payment_received") {
      const name = n.body.split(" — ")[0] || n.body.split(" a ")[0];
      if (name && name.length < 40) return name;
    }
  }
  return "Ordre";
}

type NotificationGroupItem =
  | { kind: "single"; notif: InAppNotification }
  | { kind: "group"; groupKey: string; label: string; href: string; notifications: InAppNotification[] };

/** Group notifications by ordre or discussion, preserving chronological order of first appearance */
function groupNotifications(notifications: InAppNotification[]): NotificationGroupItem[] {
  const items: NotificationGroupItem[] = [];
  const keyMap = new Map<string, number>(); // groupKey → index in items array

  for (const notif of notifications) {
    const key = getGroupKey(notif);
    if (!key) {
      items.push({ kind: "single", notif });
      continue;
    }

    const existingIdx = keyMap.get(key);
    if (existingIdx !== undefined) {
      const group = items[existingIdx];
      if (group.kind === "group") {
        group.notifications.push(notif);
      }
    } else {
      const idx = items.length;
      keyMap.set(key, idx);
      items.push({
        kind: "group",
        groupKey: key,
        label: "",
        href: notif.href,
        notifications: [notif],
      });
    }
  }

  // Compute labels and flatten single-item groups back to singles
  return items.map((item) => {
    if (item.kind === "group" && item.notifications.length === 1) {
      return { kind: "single", notif: item.notifications[0] };
    }
    if (item.kind === "group") {
      item.label = extractGroupLabel(item.groupKey, item.notifications);
    }
    return item;
  });
}

function NotificationItem({
  notif,
  onDismiss,
  compact,
}: {
  notif: InAppNotification;
  onDismiss: (id: string) => void;
  compact?: boolean;
}) {
  const markRead = useInAppNotificationsStore((s) => s.markRead);

  const handleClick = () => {
    markRead(notif.id);
    navigateTo({ route: notif.href });
  };

  return (
    <div
      className={`flex flex-row items-start gap-3 ${compact ? "px-2 py-2" : "px-3 py-3"} rounded-xl cursor-pointer transition-colors hover:bg-shadow_5 ${!notif.read ? "bg-blue-50" : ""}`}
      onClick={handleClick}
    >
      <div
        className={`flex items-center justify-center ${compact ? "w-6 h-6" : "w-8 h-8"} rounded-lg flex-shrink-0 mt-0.5`}
        style={{ backgroundColor: `${EVENT_COLORS[notif.type]}18` }}
      >
        <Icon1
          name={EVENT_ICONS[notif.type] || "cloche"}
          size={compact ? 12 : 16}
          color={EVENT_COLORS[notif.type]}
        />
      </div>
      <div className="flex flex-col flex-1 min-w-0">
        <div className="flex flex-row items-center justify-between gap-2">
          <span className={`${compact ? "text-h4" : "text-h3"} font-semibold text-neutral-900 truncate`}>
            {notif.title}
          </span>
          {!notif.read && (
            <div className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0" />
          )}
        </div>
        <span className={`${compact ? "text-h5" : "text-h4"} text-neutral-500 line-clamp-2 mt-0.5`}>
          {notif.body}
        </span>
        <span className={`${compact ? "text-h5" : "text-h4"} text-neutral-400 mt-1`}>
          {timeAgo(notif.createdAt)}
        </span>
      </div>
      <button
        className="opacity-0 hover:opacity-100 flex-shrink-0 p-1 rounded hover:bg-shadow_10 transition-opacity"
        onClick={(e) => {
          e.stopPropagation();
          onDismiss(notif.id);
        }}
      >
        <Icon1 name="effacer2" size={14} color={colors.neutral_400} />
      </button>
    </div>
  );
}

function NotificationGroupCard({
  group,
  onDismiss,
}: {
  group: Extract<NotificationGroupItem, { kind: "group" }>;
  onDismiss: (id: string) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const markRead = useInAppNotificationsStore((s) => s.markRead);

  const hasUnread = group.notifications.some((n) => !n.read);
  const uniqueTypes = Array.from(new Set(group.notifications.map((n) => n.type)));
  const latestTime = group.notifications[0].createdAt;
  const isDiscussion = group.groupKey.startsWith("discussion:");
  const countLabel = isDiscussion
    ? `${group.notifications.length} messages`
    : `${group.notifications.length} événements`;

  const handleHeaderClick = () => {
    setExpanded((v) => !v);
  };

  const handleNavigate = (e: React.MouseEvent) => {
    e.stopPropagation();
    group.notifications.forEach((n) => { if (!n.read) markRead(n.id); });
    navigateTo({ route: group.href });
  };

  return (
    <div className={`rounded-xl transition-colors ${hasUnread ? "bg-blue-50" : ""}`}>
      {/* Group header */}
      <div
        className="flex flex-row items-center gap-3 px-3 py-3 cursor-pointer rounded-xl hover:bg-shadow_5 transition-colors"
        onClick={handleHeaderClick}
      >
        {/* Icon strip */}
        <div className="flex flex-row items-center gap-1 flex-shrink-0">
          {uniqueTypes.map((type) => (
            <div
              key={type}
              className="flex items-center justify-center w-7 h-7 rounded-lg"
              style={{ backgroundColor: `${EVENT_COLORS[type]}18` }}
            >
              <Icon1
                name={EVENT_ICONS[type] || "cloche"}
                size={14}
                color={EVENT_COLORS[type]}
              />
            </div>
          ))}
        </div>

        <div className="flex flex-col flex-1 min-w-0">
          <div className="flex flex-row items-center justify-between gap-2">
            <span className="text-h3 font-semibold text-neutral-900 truncate">
              {group.label}
            </span>
            <div className="flex flex-row items-center gap-2">
              {hasUnread && (
                <div className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0" />
              )}
              <div
                className="transition-transform duration-200"
                style={{ transform: expanded ? "rotate(180deg)" : "rotate(0deg)" }}
              >
                <Icon1
                  name="arrow-down"
                  size={12}
                  color={colors.neutral_400}
                />
              </div>
            </div>
          </div>
          <div className="flex flex-row items-center gap-2 mt-0.5">
            <span className="text-h4 text-neutral-500">
              {countLabel}
            </span>
            <span className="text-h4 text-neutral-400">
              {timeAgo(latestTime)}
            </span>
          </div>
        </div>

        <button
          className="opacity-0 hover:opacity-100 flex-shrink-0 p-1 rounded hover:bg-shadow_10 transition-opacity"
          onClick={handleNavigate}
          title={isDiscussion ? "Voir la discussion" : "Voir l'ordre"}
        >
          <Icon1 name="arrow-right" size={14} color={colors.neutral_400} />
        </button>
      </div>

      {/* Expanded: show individual notifications */}
      {expanded && (
        <div className="flex flex-col ml-4 border-l-2 border-shadow_10 mb-1">
          {group.notifications.map((n) => (
            <NotificationItem
              key={n.id}
              notif={n}
              onDismiss={onDismiss}
              compact
            />
          ))}
        </div>
      )}
    </div>
  );
}

/** Floating toast that appears for 5s when a new notification arrives */
function NotificationToast({
  notif,
  onDone,
}: {
  notif: InAppNotification;
  onDone: () => void;
}) {
  const [visible, setVisible] = useState(false);
  const markRead = useInAppNotificationsStore((s) => s.markRead);

  useEffect(() => {
    requestAnimationFrame(() => setVisible(true));
    const timer = setTimeout(() => {
      setVisible(false);
      setTimeout(onDone, 300);
    }, TOAST_DURATION);
    return () => clearTimeout(timer);
  }, []);

  const handleClick = () => {
    markRead(notif.id);
    navigateTo({ route: notif.href });
    setVisible(false);
    setTimeout(onDone, 300);
  };

  const color = EVENT_COLORS[notif.type] || colors.neutral_500;

  return (
    <div
      onClick={handleClick}
      className={`flex flex-row items-center gap-3 px-4 py-3 bg-white rounded-2xl shadow-lg border-1 border-shadow_10 cursor-pointer transition-all duration-300 ease-out w-300 ${
        visible
          ? "opacity-100 translate-y-0"
          : "opacity-0 -translate-y-2"
      }`}
    >
      <div
        className="flex items-center justify-center w-8 h-8 rounded-lg flex-shrink-0"
        style={{ backgroundColor: `${color}18` }}
      >
        <Icon1
          name={EVENT_ICONS[notif.type] || "cloche"}
          size={16}
          color={color}
        />
      </div>
      <div className="flex flex-col flex-1 min-w-0">
        <span className="text-h3 font-semibold text-neutral-900 truncate">
          {notif.title}
        </span>
        <span className="text-h4 text-neutral-500 truncate">
          {notif.body}
        </span>
      </div>
    </div>
  );
}

export default function NotificationBell({ sidebarMode }: { sidebarMode?: boolean } = {}) {
  const [open, setOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [toast, setToast] = useState<InAppNotification | null>(null);
  const prevCountRef = useRef(0);

  const notifications = useInAppNotificationsStore((s) => s.notifications);
  const markAllRead = useInAppNotificationsStore((s) => s.markAllRead);
  const dismiss = useInAppNotificationsStore((s) => s.dismiss);
  const clearAll = useInAppNotificationsStore((s) => s.clearAll);

  const unreadCount = notifications.filter((n) => !n.read).length;
  const grouped = useMemo(() => groupNotifications(notifications), [notifications]);

  // Show toast when a new notification appears
  useEffect(() => {
    if (notifications.length > prevCountRef.current && notifications.length > 0) {
      const latest = notifications[0];
      if (!latest.read && !open) {
        setToast(latest);
      }
    }
    prevCountRef.current = notifications.length;
  }, [notifications.length]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (
        panelRef.current &&
        !panelRef.current.contains(e.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const [hovered, setHovered] = useState(false);

  return (
    <div className="relative">
      {sidebarMode ? (
        <div
          ref={buttonRef as any}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          onClick={() => setOpen((v) => !v)}
          className="flex flex-row gap-4 hover:bg-neutral-50 hover:ring-neutral-200 rounded-lg items-center cursor-pointer w-full px-2 py-1.5"
          style={{
            color: hovered || open ? colors.black : colors.neutral_400,
          }}
        >
          <Icon1
            name="cloche"
            size={22}
            color={hovered || open ? colors.black : colors.neutral_400}
          />
          <div className="text-h2 font-medium">Notifications</div>
          {unreadCount > 0 && (
            <div className="ml-auto flex items-center gap-1">
              <div className="w-2 h-2 rounded-full bg-indigo-500" />
              <span className="text-h4 font-medium text-indigo-500">
                {unreadCount}
              </span>
            </div>
          )}
        </div>
      ) : (
        <button
          ref={buttonRef}
          onClick={() => setOpen((v) => !v)}
          className="relative flex items-center justify-center w-9 h-9 rounded-xl hover:bg-shadow_5 transition-colors"
        >
          <Icon1
            name="cloche"
            size={20}
            color={open ? colors.blue_500 : colors.neutral_500}
          />
          {unreadCount > 0 && (
            <span className="absolute top-1 right-1 min-w-4 h-4 px-1 rounded-full bg-blue-500 text-white text-[10px] font-bold flex items-center justify-center leading-none">
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
        </button>
      )}

      {/* Toast preview */}
      {toast && !open && (
        <div className={sidebarMode ? "absolute left-full top-0 ml-2 z-[201]" : "absolute right-0 top-full mt-2 z-[201]"}>
          <NotificationToast
            notif={toast}
            onDone={() => setToast(null)}
          />
        </div>
      )}

      {open && (
        <div
          ref={panelRef}
          className={`absolute w-300 max-h-[520px] bg-white rounded-2xl shadow-xl border-1 border-shadow_10 flex flex-col z-[200] overflow-hidden ${
            sidebarMode ? "left-full top-0 ml-2" : "right-0 top-full mt-2"
          }`}
        >
          {/* Header */}
          <div className="flex flex-row items-center justify-between px-4 py-3 border-b-1 border-shadow_10">
            <span className="text-h2 font-semibold text-neutral-900">
              Notifications
            </span>
            <div className="flex flex-row items-center gap-2">
              {unreadCount > 0 && (
                <button
                  onClick={markAllRead}
                  className="text-h4 text-blue-500 hover:text-blue-600 transition-colors"
                >
                  Tout marquer lu
                </button>
              )}
              {notifications.length > 0 && (
                <button
                  onClick={clearAll}
                  className="text-h4 text-neutral-400 hover:text-neutral-600 transition-colors"
                >
                  Effacer
                </button>
              )}
            </div>
          </div>

          {/* List */}
          <div className="flex flex-col overflow-y-auto flex-1 p-1">
            {notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 gap-2 text-neutral-400">
                <Icon1 name="cloche" size={28} color={colors.neutral_300} />
                <span className="text-h3">Aucune notification</span>
              </div>
            ) : (
              <div className="flex flex-col">
                {grouped.map((item) =>
                  item.kind === "single" ? (
                    <NotificationItem
                      key={item.notif.id}
                      notif={item.notif}
                      onDismiss={dismiss}
                    />
                  ) : (
                    <NotificationGroupCard
                      key={item.groupKey}
                      group={item}
                      onDismiss={dismiss}
                    />
                  ),
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
